import React, {useEffect, useLayoutEffect, useRef, useState} from "react";
import {useLocation} from "react-router-dom";
import {createWebSocketClient} from "../http/http";
import DeployService from "../http/DeployService";
import {toast} from "react-toastify";
import './DeployDetailPage.css';
import {DeployState, DeployStateExt} from "../enums/DeployState";
import {DeployRecord} from "../entities/DeployRecord";
import {Button, Modal} from "antd";
import {DeployServerInfo} from "../entities/ServiceInfo";
import {ServerInfoTable} from "../components/ServerInfoTable";
import DateFormatUtils from "../utils/DateFormatUtils";

interface WebSocketMessage {
    id: number;
    type: string;
    message: string;
}

const DeployDetailPage: React.FC = () => {
    const [messages, setMessages] = useState<WebSocketMessage[]>([]);
    const [deployRecord, setDeployRecord] = useState<DeployRecord | null>(null);
    const [servers, setServers] = useState<DeployServerInfo[]>([]);
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const id = parseInt(searchParams.get("id") || '0');
    const [deployState, setDeployState] = useState<DeployState>(DeployState.PENDING);
    const [rollbackState, setRollbackState] = useState<DeployState>(DeployState.PENDING);
    const messageLogRef = useRef<HTMLPreElement | null>(null);
    const [isContinueModalVisible, setIsContinueModalVisible] = useState(false);
    const [isRollbackModalVisible, setIsRollbackModalVisible] = useState(false);
    const [selectedServers, setSelectedServers] = useState<string[]>([]);

    const setRecord = (data: DeployRecord) => {
        setDeployRecord(data);
        setServers(data.deployServers || []);
        setDeployState(DeployStateExt.fromString(data.deployState));
        setRollbackState(DeployStateExt.fromString(data.rollbackState));
    }

    useEffect(() => {
        if (id === 0) {
            return;
        }
        document.title = `部署记录`;
        DeployService.getDeployRecord({ id: id }).then((response) => {
            if (response.isFailed()) {
                return;
            }
            const data = response.data;
            if (!data) {
                toast.error("未找到部署记录");
                return;
            }
            setRecord(data);
        });
    }, [id]);

    const createWebSocket = (url: string) => {
        const socket = createWebSocketClient(url);
        socket.onopen = () => {
            console.log(`WebSocket connection to ${url} opened`);
        };
        socket.onmessage = (event) => {
            const message: WebSocketMessage = JSON.parse(event.data);
            if (message.id !== id) {
                return;
            }
            if (message.type === "DONE") {
                const newRecord = JSON.parse(message.message) as DeployRecord;
                if (deployRecord && newRecord.updateTime < deployRecord.updateTime){
                    return;
                }
                setRecord(newRecord);
                if (newRecord.deployState === DeployState[DeployState.SUCCESSFUL] || newRecord.deployState === DeployState[DeployState.FAILED]) {
                    socket.close();
                }
                return;
            }
            setMessages((prevMessages) => {
                if (message.message.endsWith("\r") && prevMessages.length > 0 && prevMessages[prevMessages.length - 1].message.endsWith("\r")) {
                    prevMessages.pop();
                }
                return [...prevMessages, message];
            });
        };
        socket.onerror = (error) => {
            console.error(`WebSocket to ${url} error:`, error);
        };
        socket.onclose = () => {
            console.log(`WebSocket connection to ${url} closed`);
        };
        return socket;
    }

    useEffect(() => {
        if (deployRecord?.deployState !== DeployState[DeployState.STARTED]) {
            return;
        }
        const socket = createWebSocket("/ws/printLog/deploy");
        return () => {
            socket.close();
        }
    }, [deployRecord]);

    useLayoutEffect(() => {
        const currentRef = messageLogRef.current;
        if (currentRef) {
            const { scrollTop, scrollHeight, clientHeight } = currentRef;
            const isAtBottom = Math.abs(scrollTop + clientHeight - scrollHeight) < 1;
            if (isAtBottom) {
                currentRef.scrollTop = scrollHeight;
            }
        }
    }, [messages]);

    const markSuccessful = () => {
        DeployService.markSuccessful({ id, rollback: false }).then((response) => {
            if (response.isFailed()) {
                return;
            }
            setDeployRecord(response.data);
            toast.success("标记成功");
            window.location.reload();
        });
    }

    const continueDeploy = () => {
        setIsContinueModalVisible(true);
    }

    const handleContinueOk = () => {
        if (selectedServers.length === 0) {
            toast.error("请选择继续部署的服务器！");
            return
        }
        DeployService.continueDeploy({ deployRecordId: id, ips: selectedServers }).then((response) => {
            if (response.isFailed()) {
                return;
            }
            toast.success("继续部署成功");
            setIsContinueModalVisible(false);
            window.location.reload();
        });
    }

    const handleContinueCancel = () => {
        setIsContinueModalVisible(false);
    }

    const rollback = () => {
        setIsRollbackModalVisible(true);
    }

    const handleRollbackOk = () => {
        DeployService.rollback({ deployRecordId: id }).then((response) => {
            if (response.isFailed()) {
                return;
            }
            toast.success("操作成功！");
            setIsRollbackModalVisible(false);
            window.location.reload();
        });
    }

    const handleRollbackCancel = () => {
        setIsRollbackModalVisible(false);
    }

    return (
        <div className="deploy-detail-page">
            {deployRecord && (
                <div>
                    <div className="deploy-detail-header">
                        <div className="section-header">部署信息</div>
                        {
                            deployState === DeployState.DEPLOYED && rollbackState === DeployState.PENDING && (<div className="deploy-detail-header-actions">
                                {rollbackState === DeployState.PENDING && <Button danger type="default" onClick={rollback}>
                                    回滚
                                </Button>}
                                {rollbackState === DeployState.PENDING && <Button type="primary" onClick={continueDeploy}>
                                    继续
                                </Button>}
                                <Button type="primary" onClick={markSuccessful}
                                        style={{backgroundColor: DeployStateExt.getStatusColor(DeployState.SUCCESSFUL)}}>
                                    完成
                                </Button>
                            </div>)
                        }
                    </div>
                    <div className="package-info">
                        <div className="package-info-column">
                            <div className="text-block"><strong>部署环境：</strong> {deployRecord.env}</div>
                            <div className="text-block">
                                <strong>创建时间：</strong> {DateFormatUtils.format(deployRecord.createTime)}
                            </div>
                            <div className="text-block">
                                <strong>状态：</strong>
                                {
                                    rollbackState === DeployState.PENDING ?
                                        <span style={{color: DeployStateExt.getStatusColor(deployState)}}>
                                            {DeployStateExt.getDeployStateText(deployState)}
                                        </span> :
                                        <span style={{color: DeployStateExt.getStatusColor(DeployState.FAILED)}}>
                                            {DeployStateExt.getRollbackStateText(rollbackState)}
                                        </span>
                                }
                            </div>
                        </div>
                        <div className="package-info-column">
                            <div className="text-block">
                                <strong>部署包：</strong> {deployRecord.packageRecord?.packageName}</div>
                            <div className="text-block">
                                <strong>更新时间：</strong> {DateFormatUtils.format(deployRecord.updateTime)}
                            </div>
                        </div>
                    </div>
                    <div className="section-header">
                        机器信息
                    </div>
                    <div className="server-info-table">
                        <ServerInfoTable servers={deployRecord.deployServers!} records={deployRecord.serverRecords}/>
                    </div>
                    <div className="section-header">
                        部署日志
                    </div>
                    <pre className="message-log" ref={messageLogRef}>
                        {deployRecord?.log ? deployRecord.log : messages.map((msg, index) => (
                            <div key={index}>
                                {msg.message}
                            </div>
                        ))}
                    </pre>
                    <Modal
                        width="60%"
                        title="选择服务器"
                        open={isContinueModalVisible}
                        onOk={handleContinueOk}
                        onCancel={handleContinueCancel}
                        okText="确认"
                        cancelText="取消"
                    >
                        <ServerInfoTable servers={servers} records={deployRecord?.serverRecords}
                                         onServerSelected={setSelectedServers}/>
                    </Modal>
                    <Modal
                        title="确认回滚"
                        open={isRollbackModalVisible}
                        onOk={handleRollbackOk}
                        onCancel={handleRollbackCancel}
                        okText="确认"
                        cancelText="取消"
                    >
                        <div>你确定要回滚这次部署吗？</div>
                    </Modal>
                </div>
            )}
        </div>
    );
};

export default DeployDetailPage;